import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { registerUser, validateReferralCode } from "api";
import useAuth from "context/useAuth";
import ReferralModal from "components/ReferralModal";

const INVALID_REFERRAL_MESSAGE =
  "Invalid referral code. Please check the code and try again.";
const REFERRAL_ERROR_MESSAGE =
  "We could not validate the referral code. Please try again.";

export default function RegisterPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    display_name: "",
    password: "",
    bonus_code: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Referral validation state. `status` is one of:
  // 'idle' | 'loading' | 'valid' | 'invalid' | 'error'.
  const [referralStatus, setReferralStatus] = useState("idle");
  const [referralModal, setReferralModal] = useState(null);

  const dismissError = () => setError("");
  const dismissSuccess = () => setSuccess("");

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && user) {
      navigate("/account");
    }
  }, [user, loading, navigate]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleReferralChange = (e) => {
    // Editing the code clears any previously validated state.
    setReferralStatus("idle");
    setForm((prev) => ({ ...prev, bonus_code: e.target.value }));
  };

  const handleValidateReferral = async () => {
    const code = form.bonus_code.trim();
    if (!code) {
      setReferralStatus("idle");
      return;
    }

    setReferralStatus("loading");
    try {
      const result = await validateReferralCode(code);
      if (result?.valid) {
        setReferralStatus("valid");
        if (result.message) {
          setReferralModal(result.message);
        }
      } else {
        setReferralStatus("invalid");
      }
    } catch {
      setReferralStatus("error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setError("");
    setSuccess("");
    setIsSubmitting(true);
    try {
      await registerUser(form);
      setSuccess("Account created successfully! Redirecting to login...");
      setTimeout(
        () => navigate("/login", { state: { registrationSuccess: true } }),
        1500,
      );
    } catch (err) {
      const serverMessage =
        err.response?.data?.message || err.response?.data?.error;
      setError(serverMessage || "Registration failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  // if (loading) return <div>Loading...</div>; // or a spinner
  if (loading) return null; // or a spinner

  return (
    <div className="login_section pt-80 pb-80 p3-bg">
      <div className="container-fluid">
        <div className="row justify-content-between align-items-center">
          <div className="col-6 d-none d-lg-block">
            <img
              className="w-100"
              src="/assets/images/register.png"
              alt="Register Visual"
            />
          </div>
          <div className="col-lg-6 col-xl-5">
            <div className="px-4 pt-4 pb-5">
              {error && (
                <div
                  role="alert"
                  className="d-flex align-items-start justify-content-between gap-3 mb-4 px-4 py-3"
                  style={{
                    backgroundColor: "rgba(255, 0, 0, 0.2)",
                    borderRadius: "50px",
                  }}
                >
                  <span className="pe-2">{error}</span>
                  <button
                    type="button"
                    onClick={dismissError}
                    aria-label="Dismiss registration error"
                    className="btn p-0 border-0 bg-transparent text-white lh-1"
                    style={{ fontSize: "1.1rem" }}
                  >
                    ×
                  </button>
                </div>
              )}
              {success && (
                <div
                  role="status"
                  className="d-flex align-items-start justify-content-between gap-3 mb-4 px-4 py-3"
                  style={{
                    backgroundColor: "rgba(0, 255, 0, 0.2)",
                    borderRadius: "50px",
                  }}
                >
                  <span className="pe-2">{success}</span>
                  <button
                    type="button"
                    onClick={dismissSuccess}
                    aria-label="Dismiss registration success"
                    className="btn p-0 border-0 bg-transparent text-white lh-1"
                    style={{ fontSize: "1.1rem" }}
                  >
                    ×
                  </button>
                </div>
              )}
              <h3>Create New Account</h3>
              <p>Sign up now and start trading smarter—and faster.</p>
              <form onSubmit={handleSubmit} className="mt-4">
                <input
                  className="n11-bg mb-3"
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
                <input
                  className="n11-bg mb-3"
                  type="text"
                  name="display_name"
                  placeholder="Name"
                  value={form.display_name}
                  onChange={handleChange}
                  required
                />
                <input
                  className="n11-bg mb-3"
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={form.password}
                  onChange={handleChange}
                  pattern="^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,20}$"
                  title="Password must be 6 to 20 characters and include at least one uppercase letter, one lowercase letter, and one number."
                  required
                />
                <div className="mb-3">
                  <div className="d-flex gap-2">
                    <input
                      className="n11-bg mb-0"
                      type="text"
                      name="bonus_code"
                      aria-label="Bonus Code"
                      placeholder="Bonus Code (optional)"
                      value={form.bonus_code}
                      onChange={handleReferralChange}
                    />
                    <button
                      type="button"
                      className="cmn-btn"
                      onClick={handleValidateReferral}
                      disabled={
                        referralStatus === "loading" || !form.bonus_code.trim()
                      }
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {referralStatus === "loading" ? "Validating…" : "Enter"}
                    </button>
                  </div>
                  {referralStatus === "valid" && (
                    <span
                      className="d-block mt-2"
                      style={{ color: "rgb(var(--g1))" }}
                    >
                      Bonus code applied.
                    </span>
                  )}
                  {referralStatus === "invalid" && (
                    <span className="d-block mt-2" style={{ color: "#ff6b6b" }}>
                      {INVALID_REFERRAL_MESSAGE}
                    </span>
                  )}
                  {referralStatus === "error" && (
                    <span className="d-block mt-2" style={{ color: "#ff6b6b" }}>
                      {REFERRAL_ERROR_MESSAGE}
                    </span>
                  )}
                </div>
                <div className="mb-3">
                  <label>
                    <input type="checkbox" required /> I agree to the Terms of
                    Use{" "}
                  </label>
                </div>
                <button
                  className="cmn-btn w-100"
                  type="submit"
                  disabled={
                    isSubmitting ||
                    (form.bonus_code.trim() !== "" &&
                      referralStatus !== "valid")
                  }
                  aria-busy={isSubmitting}
                >
                  {isSubmitting && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    ></span>
                  )}
                  {isSubmitting ? "Creating account..." : "Sign Up Now"}
                </button>
              </form>
              <span className="d-block mt-3 text-center">
                Already a member?{" "}
                <Link
                  to="/login"
                  style={{
                    color: "rgb(var(--g1))",
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                    fontWeight: 600,
                    display: "inline-block",
                  }}
                >
                  Login
                </Link>
              </span>
            </div>
          </div>
        </div>
      </div>
      {referralModal && (
        <ReferralModal
          message={referralModal}
          onClose={() => setReferralModal(null)}
        />
      )}
    </div>
  );
}
